import type { SvgIconComponent } from "@mui/icons-material";
import {
	UploadFile,
	TableChart,
	Map,
	LinkOff,
	ContentCopy,
	EditNote,
	AccountTree,
	Assignment,
	Person,
	Layers,
	GridView,
	ScatterPlot,
	SearchOff,
	Checklist,
} from "@mui/icons-material";
import type { User } from "../../../../../../models";
import type { Job, Company, Deal, Contact, Branch, Grower, Region } from "../../../../../../models";
import type { Feature, FeatureCollection, Geometry, Polygon, MultiPolygon } from "geojson";
import type { GeoJSONDataMap, GroupedGeoJSONFiles } from "../../../../../../utilities/types";
import type { PropertyMappings } from "../../../../shared/PropertyMappings.types";
import type { FileValidationWarning } from "../../components/GeoTypeSelectorGroup/GeoTypeSelectorGroup.helpers";

// ============================================================================
// Wizard Step Types
// ============================================================================

/**
 * Configuration phase steps (before job creation)
 */
export type ConfigurationStep = "file-validation" | "property-mapping" | "geo-type-selection";

/**
 * Validation phase steps (after job creation)
 */
export type ValidationStep =
	| "duplicate-boundaries"
	| "overlapping-boundaries"
	| "overlapping-zones"
	| "overlapping-points"
	| "unmatched-features"
	| "missing-features"
	| "branch-configuration"
	| "deal-configuration"
	| "salesperson-assignment"
	| "missing-invalid-data"
	| "summary";

/**
 * All wizard steps - configuration followed by validation
 */
export type WizardStep = ConfigurationStep | ValidationStep;

/**
 * Wizard phase - configuration (before jobs) or validation (after jobs)
 */
export type WizardPhase = "configuration" | "validation";

/**
 * Configuration phase steps in order
 */
export const CONFIGURATION_STEPS: ConfigurationStep[] = ["file-validation", "property-mapping", "geo-type-selection"];

/**
 * Validation phase steps in order (unmatched first, then duplicates before overlaps to avoid false positives)
 */
export const VALIDATION_STEPS: ValidationStep[] = [
	"unmatched-features",
	"duplicate-boundaries",
	"missing-invalid-data",
	"overlapping-boundaries",
	"overlapping-zones",
	"overlapping-points",
	"missing-features",
	"branch-configuration",
	"deal-configuration",
	"salesperson-assignment",
	"summary",
];

/**
 * All wizard steps in order
 */
export const WIZARD_STEPS: WizardStep[] = [...CONFIGURATION_STEPS, ...VALIDATION_STEPS];

export const WIZARD_STEP_TITLES: Record<WizardStep, string> = {
	// Configuration steps
	"file-validation": "File Upload",
	"property-mapping": "Property Mapping",
	"geo-type-selection": "Geo-Type",
	// Validation steps
	"overlapping-boundaries": "Overlapping Boundaries",
	"overlapping-zones": "Overlapping Zones",
	"duplicate-boundaries": "Duplicate Boundaries",
	"overlapping-points": "Overlapping Points",
	"unmatched-features": "Unmatched Points/Zones",
	"missing-features": "Missing Points/Zones",
	"branch-configuration": "Branches",
	"deal-configuration": "Sampling Configuration",
	"salesperson-assignment": "Salespersons",
	"missing-invalid-data": "Job Data Correction",
	summary: "Summary",
};

export const WIZARD_STEP_DESCRIPTIONS: Record<WizardStep, string> = {
	// Configuration steps
	"file-validation": "Review file parsing warnings and uploaded files",
	"property-mapping": "Configure which file properties map to job fields",
	"geo-type-selection": "Assign boundary, zone, or point types to each file group",
	// Validation steps
	"overlapping-boundaries": "Fix jobs with boundaries that overlap each other",
	"overlapping-zones": "Fix jobs with zones that overlap each other within the same job",
	"duplicate-boundaries": "Fix jobs with identical boundaries across multiple jobs",
	"overlapping-points": "Fix jobs with duplicate or overlapping points within the same job",
	"unmatched-features": "Fix jobs with points/zones that have no boundary",
	"missing-features": "Upload points or zones for jobs that only have a boundary",
	"branch-configuration": "Assign growers to branches",
	"deal-configuration": "Assign growers to deals",
	"salesperson-assignment": "Assign growers to salespersons",
	"missing-invalid-data": "Fix jobs with missing or invalid property values",
	summary: "Review and load jobs into the import table",
};

export const WIZARD_STEP_ICONS: Record<WizardStep, SvgIconComponent> = {
	"file-validation": UploadFile,
	"property-mapping": TableChart,
	"geo-type-selection": Map,
	"unmatched-features": LinkOff,
	"duplicate-boundaries": ContentCopy,
	"missing-invalid-data": EditNote,
	"branch-configuration": AccountTree,
	"deal-configuration": Assignment,
	"salesperson-assignment": Person,
	"overlapping-boundaries": Layers,
	"overlapping-zones": GridView,
	"overlapping-points": ScatterPlot,
	"missing-features": SearchOff,
	summary: Checklist,
};

// ============================================================================
// Step Help Content
// ============================================================================

export interface StepHelpContent {
	summary: string;
	actions?: string[];
}

export const WIZARD_STEP_HELP: Record<WizardStep, StepHelpContent> = {
	"file-validation": {
		summary:
			"Drag and drop your geospatial files here, or click to browse. Supported formats include Shapefile (.shp + companion files), GeoJSON, KML, KMZ, and ZIP archives.",
		actions: ["Review any parsing warnings before continuing", "Use Clear to start over if needed"],
	},
	"property-mapping": {
		summary:
			"Map properties from your uploaded files to Rogo job fields like Grower Name, Field Name, and Farm Name. The wizard will attempt to auto-detect common mappings.",
		actions: [
			"Drag-and-drop to match file properties to job fields",
			"Use Show More Fields for additional less-common fields",
		],
	},
	"geo-type-selection": {
		summary:
			"Assign a geo-type to each group of uploaded data. Polygons can be boundaries or sample zones; points can be centroids or cores.",
		actions: [
			"Select to view on map, or to perform bulk operations",
			"Filter by file name for quicker assignment",
			"Exclude groups that shouldn't be imported",
		],
	},
	"unmatched-features": {
		summary: "These points or zones don't have a matching boundary. Each job needs a boundary to proceed.",
		actions: [
			"Upload a boundary file",
			"OR Delete the job",
			"OR Assign features to a nearby job",
			"OR Skip to address later",
		],
	},
	"duplicate-boundaries": {
		summary: "These jobs share identical boundaries.",
		actions: ["Merge the jobs to combine their data", "OR Delete one of the duplicates", "OR Skip to address later"],
	},
	"missing-invalid-data": {
		summary: "These jobs have missing or invalid field values that need to be corrected before import.",
		actions: ["Edit data using the form", "OR Delete the job if it's not needed", "OR Skip to address later"],
	},
	"overlapping-boundaries": {
		summary:
			"These jobs have boundaries that overlap each other. Very small overlaps are generally not an issue, but larger ones must be fixed.",
		actions: ["Delete one of the overlapping jobs", "OR Skip to address later"],
	},
	"overlapping-zones": {
		summary: "These jobs have sample zones that overlap each other.",
		actions: ["Select and delete overlapping zones on the map", "OR Skip to address later"],
	},
	"overlapping-points": {
		summary: "These jobs have points which are either duplicate or very close.",
		actions: ["Delete the duplicate points", "OR Skip to address later"],
	},
	"missing-features": {
		summary: "These jobs have a boundary but no sampling points or zones. Points/zones are not required.",
		actions: [
			"Drag and drop point or zone files to upload",
			"OR Delete the job if it's not needed",
			"OR Skip to address later",
		],
	},
	"branch-configuration": {
		summary:
			"Assign growers to your organization's branches. Drag growers from the unassigned list into a branch card.",
		actions: [
			"Drag-and-drop growers/jobs into branches",
			"Search to filter branches by name",
			"Expand a grower to assign individual jobs to different branches",
		],
	},
	"deal-configuration": {
		summary:
			"Assign growers to sampling configurations. Use search and filters to find the right configuration by its properties.",
		actions: [
			"Drag-and-drop growers/jobs into sampling configurations",
			"Filter configurations by density, depth, or other properties",
			"Expand a grower to assign individual jobs to different configurations",
		],
	},
	"salesperson-assignment": {
		summary:
			"Assign growers to salespersons. You can also create new salespersons if needed. Salespersons are not required.",
		actions: [
			"Drag-and-drop growers/jobs into salespersons",
			"'Add Salesperson' to create one",
			"Expand a grower to assign individual jobs to different salespersons",
		],
	},
	summary: {
		summary: "Review your import before finishing.",
		actions: [
			"Check the total job count and any skipped issues.",
			"Click on a skipped issue to jump back and resolve it.",
		],
	},
};

// ============================================================================
// Issue Types
// ============================================================================

/**
 * Issue when 2+ jobs have boundaries that geospatially intersect
 */
export interface OverlapIssue {
	id: string;
	jobIds: string[];
	jobs: Job[];
	/** Intersection area in square meters (for display) */
	intersectionArea: number;
	/** The intersecting geometry for map visualization */
	intersectionGeometry?: Feature<Polygon | MultiPolygon>;
	/** Source file paths per job (jobId -> array of file paths) */
	jobFilePaths: Record<string, string[]>;
}

/**
 * Issue when the same boundaries appear in multiple jobs (cross-job duplicates)
 */
export interface DuplicateIssue {
	id: string;
	jobIds: string[];
	jobs: Job[];
	/** Type of feature that is duplicated (now always "boundary" for cross-job duplicates) */
	featureType: "boundary";
	/** Number of duplicate features */
	duplicateCount: number;
	/** Source file paths */
	filePaths: string[];
	/** Source file paths per job (jobId -> array of file paths) */
	jobFilePaths: Record<string, string[]>;
	/** The duplicate features for map visualization */
	duplicateFeatures: Feature<Geometry>[];
}

/**
 * A group of geospatially identical features within a single job
 */
export interface DuplicateGroup {
	/** Unique identifier for this group */
	id: string;
	/** Type of features in this group */
	type: "point" | "zone";
	/** The duplicate features (2+ identical) */
	features: Feature<Geometry>[];
	/** Coordinate key used to identify duplicates */
	coordinateKey: string;
	/** Feature indices in original GeoJSON (for deletion) */
	featureIndices: number[];
}

/**
 * Issue when a single job has duplicate points or zones within itself (intra-job duplicates)
 */
export interface InternalDuplicateIssue {
	id: string;
	/** The job containing internal duplicates */
	job: Job;
	/** Groups of duplicate features */
	duplicateGroups: DuplicateGroup[];
	/** Source file paths */
	filePaths: string[];
}

/**
 * A group of zones that overlap each other within a single job
 */
export interface OverlapGroup {
	/** Unique identifier for this group */
	id: string;
	/** The overlapping zone features */
	features: Feature<Polygon | MultiPolygon>[];
	/** Feature indices in original zonesGeoJSON (for deletion) */
	featureIndices: number[];
	/** Largest pairwise intersection area in square meters */
	intersectionArea: number;
}

/**
 * Issue when a single job has zones that overlap each other (intra-job zone overlaps)
 */
export interface OverlappingZonesIssue {
	id: string;
	/** The job containing overlapping zones */
	job: Job;
	/** Groups of overlapping zones */
	overlapGroups: OverlapGroup[];
	/** Source file paths */
	filePaths: string[];
}

/**
 * Issue when a job has points/zones but no boundary
 */
export interface UnmatchedIssue {
	id: string;
	job: Job;
	/** The unmatched features (points or zones) */
	unmatchedFeatures: FeatureCollection;
	/** Source file path */
	filePath: string;
	/** Jobs with boundaries within 1 mile */
	nearbyJobs: NearbyJobInfo[];
}

export interface NearbyJobInfo {
	job: Job;
	/** Distance in miles from unmatched features to this job's boundary */
	distanceMiles: number;
}

/**
 * Issue when a job has a boundary but no points or zones
 */
export interface MissingFeaturesIssue {
	id: string;
	job: Job;
}

/**
 * Issue when a job has validation errors on its properties
 */
export interface InvalidDataIssue {
	id: string;
	job: Job;
	/** Map of field name to error message */
	errors: Record<string, string>;
}

// ============================================================================
// Configuration State Types (Phase 1)
// ============================================================================

/**
 * Information about an uploaded file
 */
export interface UploadedFileInfo {
	/** File name */
	name: string;
	/** File path (may include folder structure from ZIP) */
	path: string;
	/** File size in bytes */
	size: number;
}

/**
 * Geo-type that can be assigned to a geodata group
 */
export type GeoType = "Boundary" | "Zones" | "Centroids" | "Cores";

/**
 * Geometry type of a geodata group
 */
export type GeometryType = "polygon" | "point";

/**
 * A grouped collection of geodata from files
 */
export interface GeodataGroup {
	/** Unique identifier for the group */
	id: string;
	/** Display name for the group (auto-generated or from file path) */
	displayName: string;
	/** File paths that make up this group */
	filePaths: string[];
	/** Geometry type of features in this group */
	geometryType: GeometryType;
	/** Number of features in this group */
	featureCount: number;
	/** Property names available in this group's features */
	propertyNames: string[];
	/** The GeoJSON data for this group */
	geoJSON: FeatureCollection;
}

/**
 * Configuration phase state (before job creation)
 */
export interface WizardConfigurationState {
	/** File validation warnings from parsing */
	warnings: FileValidationWarning[];
	/** List of uploaded files */
	uploadedFiles: UploadedFileInfo[];
	/** Grouped geodata from parsing */
	geodataGroups: GeodataGroup[];
	/** Property mappings per group (groupId -> mappings) */
	propertyMappings: Record<string, PropertyMappings>;
	/** Geo-type selections per group (groupId -> type or null) */
	geoTypeSelections: Record<string, GeoType | null>;
	/** Groups that have been excluded from processing */
	excludedGroupIds: Set<string>;
	/** Groups currently selected for map preview */
	selectedGroupIdsForMap: string[];
	/** Original GeoJSON data map from file parsing */
	geoDataMap: GeoJSONDataMap;
	/** Selectable geodata (categorized by geo-type) */
	selectableGeoData: GroupedGeoJSONFiles;
	/** Original files that were uploaded */
	originalFiles: File[];
}

// ============================================================================
// Wizard State
// ============================================================================

export interface WizardIssues {
	overlaps: OverlapIssue[];
	duplicateBoundaries: DuplicateIssue[];
	overlappingPoints: InternalDuplicateIssue[];
	overlappingZones: OverlappingZonesIssue[];
	unmatched: UnmatchedIssue[];
	missingFeatures: MissingFeaturesIssue[];
	invalidData: InvalidDataIssue[];
}

export interface WizardState {
	/** Current wizard phase - configuration (before jobs) or validation (after jobs) */
	phase: WizardPhase;
	/** Configuration phase state (Phase 1 - before job creation) */
	configuration: WizardConfigurationState;
	/** Current array of jobs being processed (Phase 2 - validation) */
	jobs: Job[];
	/** Current wizard step */
	currentStep: WizardStep;
	/** Current issue index within a multi-page step (0-based) */
	currentIssueIndex: number;
	/** All detected issues */
	issues: WizardIssues;
	/** IDs of issues that have been skipped by the user */
	skippedIssueIds: Set<string>;
	/** Steps that have no issues */
	emptySteps: Set<WizardStep>;
	/** Steps that have been completed */
	completedSteps: Set<WizardStep>;
	/** Steps the user has landed on (for sidebar click-to-navigate) */
	visitedSteps: Set<WizardStep>;
	/** Steps that initially had issues when wizard first ran validation (for progress display) */
	stepsWithInitialIssues: Set<WizardStep>;
	/** Whether the user manually navigated to this step via sidebar click */
	manualNavigation: boolean;
	/** Whether validation is currently running */
	isValidating: boolean;
	/** Error message if validation failed */
	validationError: string | null;
	/** If true, the next SET_ISSUES should preserve the current step */
	preserveStepOnNextValidation?: boolean;
	/** Mapping of job ID → deal ID for the deal-configuration step */
	dealAssignments: Record<string, string>;
	/** Mapping of job ID → branch ID for the branch-configuration step */
	branchAssignments: Record<string, string>;
	/** Mapping of job ID → salesperson ID for the salesperson-assignment step */
	salespersonAssignments: Record<string, string>;
	/** Per-step sets of grower names that have been "broken apart" into individual job cards */
	explodedGrowers: {
		deal: Set<string>;
		branch: Set<string>;
		salesperson: Set<string>;
	};
	/** Whether the company has branches (controls branch-configuration step visibility) */
	hasBranches: boolean;
	/** Whether salesperson assignment is required (controls salesperson-assignment step blocking) */
	isSalespersonRequired: boolean;
}

// ============================================================================
// Wizard Actions
// ============================================================================

export type WizardAction =
	// Configuration phase actions (Phase 1)
	| { type: "SET_CONFIGURATION"; configuration: WizardConfigurationState }
	| { type: "UPDATE_GEODATA"; geodata: Partial<WizardConfigurationState> }
	| { type: "CLEAR_GEODATA" }
	| { type: "UPDATE_PROPERTY_MAPPINGS"; groupId: string; mappings: PropertyMappings }
	| { type: "UPDATE_ALL_PROPERTY_MAPPINGS"; mappings: Record<string, PropertyMappings> }
	| { type: "APPLY_GLOBAL_PROPERTY_MAPPING"; field: keyof PropertyMappings; value: string[] | null }
	| { type: "UPDATE_GEO_TYPE"; groupId: string; geoType: GeoType | null }
	| { type: "UPDATE_ALL_GEO_TYPES"; updates: Record<string, GeoType | null> }
	| { type: "EXCLUDE_GROUP"; groupId: string }
	| { type: "INCLUDE_GROUP"; groupId: string }
	| { type: "SET_SELECTED_GROUPS_FOR_MAP"; groupIds: string[] }
	| { type: "COMPLETE_CONFIGURATION"; jobs: Job[] }
	// Validation phase actions (Phase 2)
	| { type: "SET_JOBS"; jobs: Job[]; preserveStep?: boolean }
	| { type: "UPDATE_JOB"; job: Job }
	| { type: "DELETE_JOB"; jobId: string }
	| { type: "DELETE_JOBS"; jobIds: string[] }
	| { type: "SKIP_ISSUE"; issueId: string }
	| { type: "UNSKIP_ISSUE"; issueId: string }
	| { type: "SKIP_ALL_STEP_ISSUES" }
	| { type: "NEXT_STEP" }
	| { type: "PREV_STEP" }
	| { type: "NEXT_ISSUE" }
	| { type: "PREV_ISSUE" }
	| { type: "GO_TO_STEP"; step: WizardStep; issueIndex?: number }
	| { type: "GO_TO_ISSUE"; index: number }
	| { type: "SET_ISSUES"; issues: WizardIssues; preserveStep?: boolean }
	| { type: "SET_DEAL_ASSIGNMENTS"; assignments: Record<string, string> }
	| { type: "SET_BRANCH_ASSIGNMENTS"; assignments: Record<string, string> }
	| { type: "SET_SALESPERSON_ASSIGNMENTS"; assignments: Record<string, string> }
	| { type: "EXPLODE_GROWER"; growerName: string; step: "deal" | "branch" | "salesperson" }
	| { type: "REGROUP_GROWER"; growerName: string; step: "deal" | "branch" | "salesperson" }
	| { type: "SET_HAS_BRANCHES"; hasBranches: boolean }
	| { type: "SET_IS_SALESPERSON_REQUIRED"; isSalespersonRequired: boolean }
	| { type: "SET_VALIDATING"; isValidating: boolean }
	| { type: "SET_VALIDATION_ERROR"; error: string | null }
	| { type: "MARK_STEP_COMPLETE"; step: WizardStep }
	| { type: "RESET" };

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Initial geodata for configuration phase (alternative to initialJobs)
 */
export interface InitialGeoData {
	/** File validation warnings from parsing */
	warnings: FileValidationWarning[];
	/** List of uploaded files */
	uploadedFiles: UploadedFileInfo[];
	/** Original GeoJSON data map from file parsing */
	geoDataMap: GeoJSONDataMap;
	/** Selectable geodata (categorized by geo-type) */
	selectableGeoData: GroupedGeoJSONFiles;
	/** Original files that were uploaded */
	originalFiles: File[];
}

export interface WizardContainerProps {
	/** Initial geodata for configuration phase (starts at file-validation step) */
	initialGeoData?: InitialGeoData;
	/** Initial jobs to validate and fix (skips configuration, starts at validation phase) */
	initialJobs?: Job[];
	/** Called when wizard completes successfully with fixed jobs */
	onComplete: (jobs: Job[]) => void;
	/** Called when wizard is cancelled */
	onCancel: () => void;
	/** Company configuration (for field visibility/required settings) */
	company?: Company;
	/** All available deals for dropdown */
	allDeals?: Deal[];
	/** Available salespersons for dropdown */
	salespersons?: Contact[];
	/** Available branches for dropdown */
	branches?: Branch[];
	/** Available regions for branch-region display */
	regions?: Region[];
	/** Pre-selected branch from parent form (applied to created jobs) */
	selectedBranch?: Branch;
	/** Available growers for autocomplete */
	growers?: Grower[];
	/** Current user (for visibility rules) */
	user?: User;
	/** Last crop options for autocomplete */
	lastCropOptions?: string[];
	/** Next crop options for autocomplete */
	nextCropOptions?: string[];
	/** When true, renders content without Dialog wrapper (for embedding in parent modal) */
	renderWithoutDialog?: boolean;
	/** Called when user clicks Start Over button (only shown when provided) */
	onStartOver?: () => void;
}

export interface StepProgressProps {
	currentStep: WizardStep;
	completedSteps: Set<WizardStep>;
	skippedSteps: Set<WizardStep>;
	issues: WizardIssues;
	/** Current wizard phase */
	phase: WizardPhase;
	/** Configuration state (for showing configuration steps) */
	configuration?: WizardConfigurationState;
	onStepClick?: (step: WizardStep) => void;
}

export interface WizardNavigationProps {
	canProceed: boolean;
	isFirstStep: boolean;
	isLastStep: boolean;
	currentIssueIndex: number;
	totalIssues: number;
	disabledReason?: string;
	onPrevious: () => void;
	onNext: () => void;
	onPreviousIssue?: () => void;
	onNextIssue?: () => void;
	onFinish?: () => void;
}

export interface IssueCardProps {
	jobs: Job[];
	issueType: "overlap" | "duplicate" | "unmatched" | "invalid";
	issueDescription: string;
	filePaths?: string[];
	onDeleteJobs?: (jobIds: string[]) => void;
	onDeleteJob?: (jobId: string) => void;
}

export interface IssuePaginationProps {
	currentIndex: number;
	totalCount: number;
	issueType: string;
	onPrevious: () => void;
	onNext: () => void;
	onGoTo: (index: number) => void;
}

// ============================================================================
// Helper Types
// ============================================================================

/**
 * Result of step validation check
 */
export interface StepValidationResult {
	isValid: boolean;
	errorMessage?: string;
}

/**
 * Configuration for each wizard step
 */
export interface StepConfig {
	step: WizardStep;
	title: string;
	description: string;
	/** Returns issues for this step */
	getIssues: (
		state: WizardState,
	) => (
		| OverlapIssue
		| DuplicateIssue
		| InternalDuplicateIssue
		| OverlappingZonesIssue
		| UnmatchedIssue
		| MissingFeaturesIssue
		| InvalidDataIssue
	)[];
	/** Checks if step should be shown (has issues) */
	hasIssues: (state: WizardState) => boolean;
	/** Checks if all issues in step are resolved */
	isComplete: (state: WizardState) => boolean;
}

/**
 * Form context props passed from parent for JobDetailModal integration
 */
export interface FormContextProps {
	company?: Company;
	allDeals?: Deal[];
	salespersons?: Contact[];
	branches?: Branch[];
	regions?: Region[];
	growers?: Grower[];
	user?: User;
	lastCropOptions?: string[];
	nextCropOptions?: string[];
}

/**
 * Context value for wizard state sharing
 */
export interface WizardContextValue {
	state: WizardState;
	dispatch: (action: WizardAction) => void;
	stepConfigs: StepConfig[];
	currentStepConfig: StepConfig | null;
	canProceedToNextStep: boolean;
	getActiveIssuesForCurrentStep: () => (
		| OverlapIssue
		| DuplicateIssue
		| InternalDuplicateIssue
		| OverlappingZonesIssue
		| UnmatchedIssue
		| MissingFeaturesIssue
		| InvalidDataIssue
	)[];
	/** Form context for JobDetailModal integration */
	formContext: FormContextProps;
}
